import { useState, useEffect, useMemo, useCallback } from 'react';
import { skillsApi } from '../services/api';
import { useAlert } from '../contexts/AlertContext';
import { REVIEW_TYPES } from '../constants/reviewTypes';
import { getDefaultLevelId } from '../constants/skillLevels';

const useAssignmentDetails = (assignmentId, employeeId) => {
  const { showAlert } = useAlert();
  
  // State management
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [competencies, setCompetencies] = useState({
    reviewers: {}
  });
  const [currentSkillTemplate, setCurrentSkillTemplate] = useState(null);
  const [loadingSkillTemplate, setLoadingSkillTemplate] = useState(false);
  const [skillLevels, setSkillLevels] = useState(null);

  // Computed values
  const myReviewer = useMemo(() => {
    if (!assignment?.reviewers) return null;
    return assignment.reviewers.find(reviewer => reviewer.employee.id === employeeId);
  }, [assignment, employeeId]);

  const defaultLevelId = getDefaultLevelId(skillLevels);

  // Helper functions
  const isSelfAssessment = () => {
    return myReviewer?.order === 1;
  };

  const isParallelReview = () => {
    return myReviewer?.reviewType === REVIEW_TYPES.PARALLEL;
  };

  const isAssessmentSubmitted = () => {
    return myReviewer?.completedAt;
  };

  const isAssignmentCompleted = () => {
    return assignment?.completedAt;
  };

  // ADR-014 §7b: the parent feedback session was closed early (snapshot close).
  // The assignment stays viewable but is frozen — every data-entry affordance
  // in the stepper renders read-only and the API rejects the writes with 409.
  const isSessionLocked = () => {
    return !!(assignment?.isLocked || assignment?.feedbackSession?.completedAt);
  };

  // Data fetching functions
  const fetchAssignment = async () => {
    try {
      setLoading(true);
      const detailedAssignment = await skillsApi.getEmployeeAssignmentById(assignmentId, employeeId);
      setAssignment(detailedAssignment);

      // Initialize competencies from all reviewers' responses
      if (detailedAssignment?.reviewers?.length > 0) {
        const newCompetencies = {
          reviewers: {}
        };

        // Process each reviewer's responses
        detailedAssignment.reviewers.forEach(reviewer => {
          // Initialize reviewer if not exists
          if (!newCompetencies.reviewers[reviewer.id]) {
            newCompetencies.reviewers[reviewer.id] = {
              isCurrent: reviewer.reviewType === REVIEW_TYPES.SEQUENTIAL ?
                reviewer.id === detailedAssignment.currentReviewerRole.id
                : reviewer.employee.id === employeeId,
              skills: {}
            };
          }

          // Process each response for this reviewer
          reviewer.responses?.forEach(response => {
            const skillTemplateId = response.skillsTemplate.id;

            // Initialize skill if not exists
            if (!newCompetencies.reviewers[reviewer.id].skills[skillTemplateId]) {
              newCompetencies.reviewers[reviewer.id].skills[skillTemplateId] = {
                overall: response.competencyLevelId,
                segments: {},
                comments: response.comments,
              };
            }
            
            // Set segment mapping competency levels
            response.segmentMappings?.forEach(mapping => {
              newCompetencies.reviewers[reviewer.id].skills[skillTemplateId].segments[mapping.segment.id] = {
                competencyLevelId: mapping.competencyLevelId,
                comments: mapping.comments || ''
              };
            });
          });
        });

        setCompetencies(newCompetencies);
      }
    } catch (error) {
      console.error('Error fetching assignment details:', error);
      showAlert(error.message || 'Failed to fetch assignment details', 'error', error);
      throw error; // Re-throw so caller can handle navigation
    } finally {
      setLoading(false);
    }
  };

  const fetchSkillTemplateDetails = useCallback(async (activeStep) => {
    const totalSkillSteps = myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length || 0;
    const isSummaryStep = activeStep === totalSkillSteps + 1;
    const isValidSkillStep = activeStep > 0 && activeStep <= totalSkillSteps;

    // Fetch for valid skill steps OR summary step (use first skill for summary to get skill levels)
    if (
      (isValidSkillStep || isSummaryStep) &&
      myReviewer?.skillsMatrixTemplate?.skillsTemplates?.[0]?.id
    ) {
      try {
        setLoadingSkillTemplate(true);
        // For summary step, use first skill template to get skill levels
        const stepIndex = isSummaryStep ? 0 : activeStep - 1;
        const skillId = myReviewer.skillsMatrixTemplate.skillsTemplates[stepIndex].skill.id;
        const versionId = myReviewer.skillsMatrixTemplate.id;
        const skillTemplateDetails = await skillsApi.getSkillTemplate(versionId, skillId);

        // Sort the competencies by level (including handling negative values)
        skillTemplateDetails.competencies.sort((a, b) => a.competencyLevel.level - b.competencyLevel.level);
        const skillLevels = skillTemplateDetails.competencies.map(competency => ({
          level: competency.competencyLevel.level,
          displayName: competency.competencyLevel.name,
          color: competency.competencyLevel.color,
          defaultDescription: competency.competencyLevel.description,
          description: competency.description,
          levelId: competency.competencyLevel.id
        }));

        setSkillLevels(skillLevels);
        setCurrentSkillTemplate(skillTemplateDetails);
      } catch (error) {
        console.error('Error fetching skill template details:', error);
        showAlert('Failed to fetch skill competency details', 'warning');
      } finally {
        setLoadingSkillTemplate(false);
      }
    }
  }, [myReviewer?.skillsMatrixTemplate?.skillsTemplates, myReviewer?.skillsMatrixTemplate?.id, showAlert]);

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const getSkillLevelsBySegmentId = useCallback((segmentId) => {
    return currentSkillTemplate?.mappings?.find(mapping => mapping.segmentId === segmentId)?.competencies?.map(competency => {
      let defaultDescription = competency.competencyLevel.description;
      
      // If defaultDescription is null, find it from skillLevels array
      if (defaultDescription === null) {
        const matchingSkillLevel = skillLevels?.find(skillLevel => skillLevel.levelId === competency.competencyLevel.id);
        defaultDescription = matchingSkillLevel?.defaultDescription || null;
      }
      
      return {
        level: competency.competencyLevel.level,
        displayName: competency.competencyLevel.name,
        color: competency.competencyLevel.color,
        defaultDescription: defaultDescription,
        description: competency.description,
        levelId: competency.competencyLevel.id
      };
    }).sort((a, b) => a.level - b.level) || [];
  }, [currentSkillTemplate, skillLevels]);

  const getCustomDescriptionsBySegmentId = useCallback((segmentId) => {
    const skillLevels = getSkillLevelsBySegmentId(segmentId);
    return skillLevels.reduce((acc, comp) => {
      acc[comp.levelId] = comp.description || comp.defaultDescription;
      return acc;
    }, {});
  }, [getSkillLevelsBySegmentId]);

  const getCustomDescriptionsBySkill = useCallback(() => {
    return currentSkillTemplate?.competencies?.reduce((acc, comp) => {
      acc[comp.competencyLevelId] = comp.description;
      return acc;
    }, {}) || {};
  }, [currentSkillTemplate]);

  // Initial data fetch
  useEffect(() => {
    fetchAssignment();
  }, [assignmentId, employeeId, refreshTrigger]);

  return {
    // State
    assignment,
    setAssignment,
    loading,
    competencies,
    setCompetencies,
    currentSkillTemplate,
    loadingSkillTemplate,
    skillLevels,
    defaultLevelId,
    
    // Computed values
    myReviewer,
    
    // Helper functions
    isSelfAssessment,
    isParallelReview,
    isAssessmentSubmitted,
    isAssignmentCompleted,
    isSessionLocked,
    
    // Actions
    fetchSkillTemplateDetails,
    triggerRefresh,
    getSkillLevelsBySegmentId,
    getCustomDescriptionsBySegmentId,
    getCustomDescriptionsBySkill
  };
};

export default useAssignmentDetails; 